import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/**
 * GrandfatherLockBadge
 * ────────────────────
 * Tiny pill that appears in the global navbar when the logged-in user
 * has at least one HustleConnect grandfather price-lock saving them
 * money vs the current market rate. Hovers to show total $ saved/mo.
 *
 * Quiet when:
 *  • The user is logged out
 *  • The user has no locks
 *  • Every lock is at the current market price (no savings to brag about)
 *
 * Tap-target: links to /account/hustleconnect?tab=pricing so the user
 * can see the full breakdown.
 */
export default function GrandfatherLockBadge() {
  const [savings, setSavings] = useState(null);
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/pricing/me`, {
          credentials: "include",
        });
        if (!r.ok) return;
        const j = await r.json();
        const locked = (j.monthly || []).filter(
          (p) => p.is_grandfathered && (p.savings_vs_market || 0) > 0,
        );
        const total = locked.reduce(
          (sum, p) => sum + (p.savings_vs_market || 0), 0);
        if (!cancelled) {
          setSavings(total);
          setCount(locked.length);
        }
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!savings || savings <= 0 || count <= 0) return null;

  return (
    <Link
      to="/account/hustleconnect"
      data-testid="grandfather-lock-badge"
      title={`Your ${count} grandfather price lock${count === 1 ? "" : "s"} `
            + `save you $${savings.toFixed(0)}/mo vs current market rates.`}
      className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-full
                 bg-emerald-50 hover:bg-emerald-100 text-emerald-800
                 border border-emerald-200 transition-colors text-xs font-semibold"
    >
      <span aria-hidden="true">🔒</span>
      <span>Saving ${savings.toFixed(0)}/mo</span>
    </Link>
  );
}
